import type {
	BankAccountApi,
	CreditFacilityApi,
	RestrictedFundApi,
	UpdateBankAccountRequestApi,
	CreditFacilityRequestApi,
	RestrictedFundRequestApi,
} from "../models/bankAccount";
import type {
	BankAccount,
	CreditFacility,
	CreditFacilityType,
	RestrictedFund,
	RestrictionType,
} from "../../types";

// ─── API → Domain ───────────────────────────────────────────────────────────

function mapCreditFacilityFromApi(
	api: CreditFacilityApi,
): CreditFacility {
	return {
		type: api.type as CreditFacilityType,
		facilityLimit: api.facilityLimit,
		facilityDrawn: api.facilityDrawn,
		// facilityRate removed — replaced by interestRateSchedule
		facilityReviewDate: api.facilityReviewDate
			? new Date(api.facilityReviewDate)
			: undefined,
		securedOn: api.securedOn,
	};
}

function mapRestrictedFundFromApi(
	api: RestrictedFundApi,
): RestrictedFund {
	return {
		id: String(api.id),
		restrictionType: api.restrictionType as RestrictionType,
		label: api.label,
		restrictedAmount: api.restrictedAmount,
		isLegal: api.isLegal,
		releaseDate: api.releaseDate ? new Date(api.releaseDate) : undefined,
		noticePeriodDays: api.noticePeriodDays,
	};
}

export function mapBankAccountFromApi(api: BankAccountApi): BankAccount {
	return {
		id: String(api.id),
		name: api.name,
		bank: api.bank,
		balance: api.balance,
		currency: api.currencyCode,
		type: api.accountType ?? undefined,
		creditFacility: api.creditFacility
			? mapCreditFacilityFromApi(api.creditFacility)
			: undefined,
		restrictedFunds: api.restrictedFunds?.map(mapRestrictedFundFromApi),
	};
}

// ─── Domain → API request ───────────────────────────────────────────────────

function mapCreditFacilityToRequest(
	facility: CreditFacility,
): CreditFacilityRequestApi {
	return {
		type: facility.type,
		facilityLimit: facility.facilityLimit,
		facilityDrawn: facility.facilityDrawn,
		// facilityRate removed — replaced by interestRateSchedule (backend wiring pending)
		facilityReviewDate: facility.facilityReviewDate
			? facility.facilityReviewDate.toISOString().split("T")[0]
			: undefined,
		securedOn: facility.securedOn,
	};
}

function mapRestrictedFundToRequest(
	fund: RestrictedFund,
): RestrictedFundRequestApi {
	// IDs that look like "r-<timestamp>" are new (created client-side); send without id
	const numericId = Number(fund.id);
	return {
		id: Number.isNaN(numericId) ? undefined : numericId,
		restrictionType: fund.restrictionType,
		label: fund.label,
		restrictedAmount: fund.restrictedAmount,
		isLegal: fund.isLegal,
		releaseDate: fund.releaseDate
			? new Date(fund.releaseDate).toISOString().split("T")[0]
			: undefined,
		noticePeriodDays: fund.noticePeriodDays,
	};
}

export function mapBankAccountToUpdateRequest(
	account: BankAccount,
): UpdateBankAccountRequestApi {
	return {
		creditFacility: account.creditFacility
			? mapCreditFacilityToRequest(account.creditFacility)
			: undefined,
		restrictedFunds: account.restrictedFunds?.map(mapRestrictedFundToRequest),
	};
}
